import {
  CompleteEncounterResponseDTO, CompleteEncounterResponseSchema,
  NpcContinuationResponseDTO, NpcContinuationResponseSchema,
  SkillCheckOutcome,
  UpdateNodeResponseDTO, UpdateNodeResponseSchema,
} from '@hexcrawl/shared-utils';
import {requestJson} from '@/ui/services/api';
import {useGameStore} from '../stores/gameStore';
import {useEncounterStore} from '../stores/encounterStore';
import {useQuestStore} from '../stores/questStore';
import {LoadingKeys, useUiStore} from '../stores/uiStore';

export const encounterService = {
  updateNode(
    encounterId: string,
    nodeId: string | null,
    cheatForceSkillCheck?: SkillCheckOutcome,
  ): Promise<UpdateNodeResponseDTO> {
    return requestJson<UpdateNodeResponseDTO>({
      url: '/game/encounter/update-node',
      method: 'POST',
      data: { encounterId, nodeId, ...(cheatForceSkillCheck && { cheatForceSkillCheck }) },
      schema: UpdateNodeResponseSchema,
    });
  },

  completeEncounter(encounterId: string): Promise<CompleteEncounterResponseDTO> {
    return requestJson<CompleteEncounterResponseDTO>({
      url: '/game/encounter/complete',
      method: 'POST',
      data: { encounterId },
      schema: CompleteEncounterResponseSchema,
    });
  },

  startNpcContinuation(gameId: string, npcId: string): Promise<NpcContinuationResponseDTO> {
    return requestJson<NpcContinuationResponseDTO>({
      url: '/game/encounter/npc-continuation',
      method: 'POST',
      data: { gameId, npcId },
      schema: NpcContinuationResponseSchema,
    });
  },
};

/**
 * Open a fresh follow-on conversation with a quest giver just after a turn-in. The
 * server writes the intro to read as an unbroken continuation (no greeting, no
 * arrival). Called once the completion encounter and any reward loot have been
 * dismissed. Shows a brief loading overlay while the encounter generates; if the
 * server has no follow-up to offer, it quietly does nothing.
 *
 * A free orchestration function (not a pure API binding) that ties together the two
 * requests above with the game/encounter/ui stores, mirroring `launchCombatScene` in
 * combatService.ts.
 */
export async function openNpcContinuation(npcId: string): Promise<void> {
  const gameId = useGameStore.getState().gameState?.id;
  if (!gameId) return;

  // Message omitted — the overlay's dots carry the "waiting" signal on their own.
  useUiStore.getState().startLoading(LoadingKeys.NPC_CONTINUATION);
  try {
    const { encounter, npcName } = await encounterService.startNpcContinuation(gameId, npcId);
    if (encounter) {
      const response = await encounterService.updateNode(encounter.id, null);
      useEncounterStore.getState().setActiveEncounter(response.encounter, response.node, npcName);
    } else {
      // No follow-up opened — flush any quest badge pulse deferred through the loot flow.
      useQuestStore.getState().flushBadgePulse();
    }
  } catch (err) {
    console.error('Failed to open NPC continuation encounter:', err);
    useQuestStore.getState().flushBadgePulse();
  } finally {
    useUiStore.getState().stopLoading(LoadingKeys.NPC_CONTINUATION);
  }
}
